import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, insertRow, updateRow } from '../db/engine';
import { FileText, Upload, Download, Trash2, X } from 'lucide-react';
import { abrirDocumento } from '../utils/archivos';

const CATEGORIA_LABEL = {
  cliente_natural:  'Cliente Natural',
  cliente_juridico: 'Cliente Jurídico',
  poliza:           'Póliza',
  solicitud:        'Solicitud',
  auto:             'Auto',
  reclamo:          'Reclamo',
  cobro:            'Cobros',
  otro:             'Otros',
};

/**
 * DocManager — Componente reutilizable de gestión documental
 *
 * Props:
 *   clienteId, polizaId, solicitudId, reclamoId  — IDs de contexto (opcionales)
 *   categoriasPermitidas  — array de strings para filtrar tipos_documento
 *   linkedClienteId       — cuando se sube en contexto no-cliente, también enlaza al cliente
 *   readOnly              — boolean
 *   tipoCliente           — 'natural' | 'juridica' (para filtrar categorías cliente)
 */
export default function DocManager({
  clienteId,
  polizaId,
  solicitudId,
  reclamoId,
  categoriasPermitidas,
  linkedClienteId,
  readOnly = false,
  tipoCliente,
}) {
  const [tipos, setTipos]           = useState([]);
  const [docs, setDocs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [uploading, setUploading]   = useState(false);

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [upTipoId, setUpTipoId]     = useState('');
  const [upAnio, setUpAnio]         = useState(new Date().getFullYear());
  const [upFile, setUpFile]         = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null); // vista previa del archivo antes de subir
  const fileRef                     = useRef(null);

  // Delete modal state
  const [deleteDoc, setDeleteDoc]   = useState(null);
  const [delMotivo, setDelMotivo]   = useState('');
  const [deleting, setDeleting]     = useState(false);

  // Buscador de tipo de documento
  const [tipoQuery, setTipoQuery]   = useState('');
  const [tipoOpen, setTipoOpen]     = useState(false);
  const tipoSearchRef               = useRef(null);
  const tipoContainerRef            = useRef(null);

  // ── Carga tipos_documento ────────────────────────────────────────
  const loadTipos = useCallback(async () => {
    let q = supabase.from('tipos_documento').select('*').eq('activo', true).order('categoria').order('orden');
    if (categoriasPermitidas && categoriasPermitidas.length > 0) {
      q = q.in('categoria', categoriasPermitidas);
    }
    const { data } = await q;
    setTipos(data || []);
  }, [categoriasPermitidas]);

  // ── Carga documentos existentes ──────────────────────────────────
  const loadDocs = useCallback(async () => {
    if (!clienteId && !polizaId && !solicitudId && !reclamoId) return;
    setLoading(true);

    // Construimos OR de los IDs disponibles
    const orParts = [];
    if (clienteId)   orParts.push(`cliente_id.eq.${clienteId}`);
    if (polizaId)    orParts.push(`poliza_id.eq.${polizaId}`);
    if (solicitudId) orParts.push(`solicitud_id.eq.${solicitudId}`);
    if (reclamoId)   orParts.push(`reclamo_id.eq.${reclamoId}`);

    const { data } = await supabase
      .from('documentos')
      .select('*, tipos_documento(nombre, codigo, categoria, requiere_anio)')
      .or(orParts.join(','))
      .eq('activo', true)
      .order('creado_en', { ascending: false });

    // Si hay categoriasPermitidas, filtramos también por categoría del tipo
    let result = data || [];
    if (categoriasPermitidas && categoriasPermitidas.length > 0) {
      result = result.filter(d => d.tipos_documento && categoriasPermitidas.includes(d.tipos_documento.categoria));
    }
    setDocs(result);
    setLoading(false);
  }, [clienteId, polizaId, solicitudId, reclamoId, categoriasPermitidas]);

  useEffect(() => { loadTipos(); }, [loadTipos]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // Genera/limpia la URL de vista previa cuando cambia el archivo seleccionado.
  useEffect(() => {
    if (!upFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(upFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [upFile]);

  // ── Helpers ─────────────────────────────────────────────────────
  const tipoSeleccionado = tipos.find(t => String(t.id) === String(upTipoId));
  const requiereAnio     = tipoSeleccionado?.requiere_anio || false;

  // Cierra el dropdown de tipo al hacer click afuera
  useEffect(() => {
    const handler = (e) => {
      if (tipoContainerRef.current && !tipoContainerRef.current.contains(e.target)) {
        setTipoOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const resetUpload = () => {
    setUpTipoId('');
    setUpAnio(new Date().getFullYear());
    setUpFile(null);
    setTipoQuery('');
    setTipoOpen(false);
    if (fileRef.current) fileRef.current.value = '';
    setShowUpload(false);
  };

  // ── Upload ───────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!upTipoId || !upFile) { alert('Selecciona el tipo de documento y el archivo.'); return; }
    if (requiereAnio && !upAnio) { alert('Este tipo de documento requiere un año.'); return; }

    setUploading(true);
    try {
      // Determina el contexto principal del path
      let ctx = 'general';
      let ctxId = 'unknown';
      if (solicitudId) { ctx = 'solicitud'; ctxId = solicitudId; }
      else if (polizaId)    { ctx = 'poliza';    ctxId = polizaId; }
      else if (clienteId)   { ctx = 'cliente';   ctxId = clienteId; }
      else if (reclamoId)   { ctx = 'reclamo';   ctxId = reclamoId; }

      const ext        = upFile.name.split('.').pop();
      const timestamp  = Date.now();
      const path       = `documentos/${ctx}/${ctxId}/${timestamp}.${ext}`;

      const { error: storageError } = await supabase.storage
        .from('solicitud-archivos')
        .upload(path, upFile, { upsert: false });

      if (storageError) throw storageError;

      // Construye el registro con todos los FK que apliquen
      const row = {
        tipo_documento_id: parseInt(upTipoId),
        nombre_archivo:    upFile.name,
        storage_path:      path,
        anio:              requiereAnio ? parseInt(upAnio) : null,
        activo:            true,
      };
      if (clienteId)                          row.cliente_id   = clienteId;
      if (linkedClienteId && !clienteId)      row.cliente_id   = linkedClienteId;
      if (polizaId)                           row.poliza_id    = polizaId;
      if (solicitudId)                        row.solicitud_id = solicitudId;
      if (reclamoId)                          row.reclamo_id   = reclamoId;

      await insertRow('documentos', row);
      resetUpload();
      await loadDocs();
    } catch (e) {
      console.error('Error subiendo documento:', e);
      alert('Error al subir el archivo: ' + (e.message || ''));
    } finally {
      setUploading(false);
    }
  };

  // ── Delete (soft) ────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!delMotivo.trim()) { alert('El motivo de eliminación es requerido.'); return; }
    setDeleting(true);
    try {
      await updateRow('documentos', deleteDoc.id, {
        activo:             false,
        eliminado_en:       new Date().toISOString(),
        motivo_eliminacion: delMotivo.trim(),
      });
      setDeleteDoc(null);
      setDelMotivo('');
      await loadDocs();
    } catch (e) {
      console.error('Error eliminando documento:', e);
      alert('Error al eliminar: ' + (e.message || ''));
    } finally {
      setDeleting(false);
    }
  };

  // ── Agrupación por categoría ─────────────────────────────────────
  const grouped = docs.reduce((acc, doc) => {
    const cat = doc.tipos_documento?.categoria || 'otro';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  // Agrupamos tipos para el optgroup del selector
  const tiposPorCategoria = tipos.reduce((acc, t) => {
    if (!acc[t.categoria]) acc[t.categoria] = [];
    acc[t.categoria].push(t);
    return acc;
  }, {});

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div>
      {/* Botón agregar */}
      {!readOnly && !showUpload && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setShowUpload(true)}
          style={{ marginBottom: 12 }}
          type="button"
        >
          <Upload size={13} /> Agregar documento
        </button>
      )}

      {/* Modal inline de upload */}
      {showUpload && (
        <div style={{
          background: 'rgba(var(--navy-rgb),0.6)',
          border: '1px solid rgba(var(--silver-rgb),0.2)',
          borderRadius: 10,
          padding: '16px',
          marginBottom: 16,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'var(--silver)', letterSpacing:1 }}>SUBIR DOCUMENTO</div>
            <button className="btn btn-icon btn-secondary btn-sm" onClick={resetUpload} type="button"><X size={14}/></button>
          </div>

          {/* Selector tipo documento con búsqueda */}
          <div className="form-group" style={{ marginBottom:10 }} ref={tipoContainerRef}>
            <label className="form-label">Tipo de Documento *</label>
            <div style={{ position:'relative' }}>
              <div style={{ position:'relative' }}>
                <FileText size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--silver)', pointerEvents:'none' }}/>
                <input
                  ref={tipoSearchRef}
                  className="form-input"
                  style={{ paddingLeft:32, paddingRight: upTipoId ? 32 : 12 }}
                  placeholder="Buscar tipo de documento..."
                  value={tipoOpen ? tipoQuery : (tipoSeleccionado ? `${tipoSeleccionado.nombre}${tipoSeleccionado.codigo ? ` (${tipoSeleccionado.codigo})` : ''}` : '')}
                  onFocus={() => { setTipoOpen(true); setTipoQuery(''); }}
                  onChange={e => { setTipoQuery(e.target.value); setTipoOpen(true); }}
                />
                {upTipoId && !tipoOpen && (
                  <button onClick={() => { setUpTipoId(''); setTipoQuery(''); }} type="button"
                    style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--silver)', display:'flex', alignItems:'center', padding:0 }}>
                    <X size={14}/>
                  </button>
                )}
              </div>
              {tipoOpen && (
                <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:1100, background:'var(--navy-grad)', border:'1px solid rgba(var(--silver-rgb),0.25)', borderRadius:8, maxHeight:260, overflowY:'auto', boxShadow:'0 8px 28px rgba(0, 0, 0, 0.5)' }}>
                  {(() => {
                    const q = tipoQuery.toLowerCase();
                    const filtrados = tipos.filter(t =>
                      !q || t.nombre.toLowerCase().includes(q) || (t.codigo||'').toLowerCase().includes(q) || (CATEGORIA_LABEL[t.categoria]||'').toLowerCase().includes(q)
                    );
                    if (filtrados.length === 0) return (
                      <div style={{ padding:'10px 14px', color:'var(--silver)', fontSize:13 }}>Sin resultados</div>
                    );
                    // Agrupar por categoría
                    const porCat = filtrados.reduce((acc, t) => {
                      if (!acc[t.categoria]) acc[t.categoria] = [];
                      acc[t.categoria].push(t);
                      return acc;
                    }, {});
                    return Object.entries(porCat).map(([cat, items]) => (
                      <div key={cat}>
                        <div style={{ padding:'6px 14px 4px', fontSize:10, fontWeight:700, color:'var(--sky-light)', letterSpacing:1, background:'rgba(var(--sky-rgb),0.08)' }}>
                          {CATEGORIA_LABEL[cat] || cat}
                        </div>
                        {items.map(t => (
                          <button key={t.id} type="button"
                            onMouseDown={() => { setUpTipoId(String(t.id)); setTipoQuery(''); setTipoOpen(false); }}
                            style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'8px 14px', textAlign:'left', background: String(t.id) === String(upTipoId) ? 'rgba(var(--sky-rgb),0.25)' : 'transparent', border:'none', cursor:'pointer', color:'var(--text)', fontSize:13, fontFamily:'Outfit,sans-serif' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--sky-rgb),0.18)'}
                            onMouseLeave={e => e.currentTarget.style.background = String(t.id) === String(upTipoId) ? 'rgba(var(--sky-rgb),0.25)' : 'transparent'}
                          >
                            <span>{t.nombre}</span>
                            {t.codigo && <span style={{ fontSize:11, color:'var(--sky-light)', marginLeft:8 }}>{t.codigo}</span>}
                          </button>
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Año (solo si requiere_anio) */}
          {requiereAnio && (
            <div className="form-group" style={{ marginBottom:10 }}>
              <label className="form-label">Año del Documento *</label>
              <input
                className="form-input"
                type="number"
                min="2000"
                max="2099"
                value={upAnio}
                onChange={e => setUpAnio(e.target.value)}
                style={{ maxWidth: 120 }}
              />
            </div>
          )}

          {/* File picker */}
          <div className="form-group" style={{ marginBottom:12 }}>
            <label className="form-label">Archivo *</label>
            <input
              ref={fileRef}
              type="file"
              style={{ display:'none' }}
              onChange={e => setUpFile(e.target.files?.[0] || null)}
            />
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => fileRef.current?.click()}
                type="button"
              >
                Seleccionar archivo
              </button>
              {upFile && (
                <span style={{ fontSize:12, color:'var(--sky-light)' }}>
                  {upFile.name}
                </span>
              )}
            </div>
          </div>

          {/* Vista previa del archivo antes de subir (para verificar que es el correcto) */}
          {upFile && previewUrl && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, marginBottom:6 }}>VISTA PREVIA</div>
              {upFile.type.startsWith('image/') ? (
                <img src={previewUrl} alt="Vista previa"
                  style={{ maxWidth:'100%', maxHeight:300, borderRadius:8, border:'1px solid rgba(var(--silver-rgb),0.2)', display:'block' }} />
              ) : upFile.type === 'application/pdf' ? (
                <iframe title="Vista previa" src={previewUrl}
                  style={{ width:'100%', height:340, border:'1px solid rgba(var(--silver-rgb),0.2)', borderRadius:8, background:'#fff' }} />
              ) : (
                <div style={{ fontSize:12, color:'var(--silver)', padding:'10px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                  Este tipo de archivo no tiene vista previa. Verifica el nombre antes de subir: <strong>{upFile.name}</strong>
                </div>
              )}
            </div>
          )}

          <div style={{ display:'flex', gap:8 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleUpload}
              disabled={uploading || !upTipoId || !upFile}
              type="button"
            >
              {uploading ? 'Subiendo...' : 'Subir'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={resetUpload} type="button">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista de documentos agrupados */}
      {loading ? (
        <div className="text-silver text-sm">Cargando documentos...</div>
      ) : docs.length === 0 ? (
        <div className="text-silver text-sm">Sin documentos registrados</div>
      ) : (
        Object.entries(grouped).map(([cat, catDocs]) => (
          <div key={cat} style={{ marginBottom: 16 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--silver)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>
              {CATEGORIA_LABEL[cat] || cat}
            </div>
            {catDocs.map(doc => {
              const tipo = doc.tipos_documento;
              const badge = tipo ? [tipo.codigo, doc.anio ? String(doc.anio) : null].filter(Boolean).join(' ') : '';
              return (
                <div key={doc.id} style={{
                  display:'flex', alignItems:'center', gap:10,
                  padding:'8px 12px',
                  background:'rgba(var(--steel-rgb),0.3)',
                  borderRadius:6, marginBottom:6,
                }}>
                  <FileText size={14} style={{ color:'var(--sky-light)', flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <a
                      href="#"
                      onClick={(e)=>{ e.preventDefault(); abrirDocumento(doc.storage_path); }}
                      title="Abrir documento"
                      style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--sky-light)', textDecoration:'none', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                    >
                      {doc.nombre_archivo}
                    </a>
                    <div style={{ fontSize:11, color:'var(--silver)' }}>
                      {tipo?.nombre || '—'}
                    </div>
                  </div>
                  {badge && (
                    <span className="badge badge-info" style={{ fontSize:10, flexShrink:0 }}>
                      {badge}
                    </span>
                  )}
                  <a
                    href="#"
                    onClick={(e)=>{ e.preventDefault(); abrirDocumento(doc.storage_path); }}
                    className="btn btn-icon btn-secondary btn-sm"
                    title="Abrir documento"
                    style={{ padding:4, flexShrink:0 }}
                  >
                    <Download size={12} />
                  </a>
                  {!readOnly && (
                    <button
                      className="btn btn-icon btn-danger btn-sm"
                      title="Eliminar"
                      style={{ padding:4, flexShrink:0 }}
                      onClick={() => { setDeleteDoc(doc); setDelMotivo(''); }}
                      type="button"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))
      )}

      {/* Modal confirmación de eliminación */}
      {deleteDoc && (
        <div className="modal-overlay" style={{ zIndex:1200 }} onClick={() => { setDeleteDoc(null); setDelMotivo(''); }}>
          <div className="modal" style={{ maxWidth:440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Eliminar documento</div>
              <button className="btn btn-icon btn-secondary btn-sm" onClick={() => { setDeleteDoc(null); setDelMotivo(''); }}>
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize:14, marginBottom:12 }}>
                ¿Confirmas que deseas eliminar <strong>{deleteDoc.nombre_archivo}</strong>?
                Esta acción no se puede deshacer.
              </div>
              <div className="form-group">
                <label className="form-label">Motivo de eliminación *</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={delMotivo}
                  onChange={e => setDelMotivo(e.target.value)}
                  placeholder="Describe el motivo..."
                  style={{ resize:'vertical' }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setDeleteDoc(null); setDelMotivo(''); }}
                disabled={deleting}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || !delMotivo.trim()}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
